import { Injectable } from "@angular/core";
import { Status, TastingContactService, translateCommonErrorStatus } from "@core/data";
import { TastingContact } from "@core/data/types/tastings-contact";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

//State model
export interface TastingContactFormStateModel {
    key: string;
    status: Status;
    data: Partial<TastingContact>;
}

//State defaults
const DEFAULTS: TastingContactFormStateModel = {
    key: null,
    status: Status.UNINITIALIZED,
    data: null,
}

//State actions
export class InitNewTastingContactForm {
    static readonly type = "[TastingContactForm] Init new";
    constructor(public defaults?: Partial<TastingContact>) {}
}

export class InitUpdateTastingContactForm {
    static readonly type = "[TastingContactForm] Init update";
    constructor(public key: string) {}
}

export class SubmitTastingContactForm {
    static readonly type = "[TastingContactForm] Submit";
    constructor(public data: Partial<TastingContact>) {}
}

//State logic
@State<TastingContactFormStateModel>({
    name: 'tasting_contact_form',
    defaults: DEFAULTS,
})
@Injectable()
export class TastingContactFormState {
    @Selector()
    static data(state: TastingContactFormStateModel) {
        return state.data;
    }

    constructor(private _tastingContactService: TastingContactService) {}

    @Action(InitNewTastingContactForm, { cancelUncompleted: true })
    initNewTastingContactForm(ctx: StateContext<TastingContactFormStateModel>, action: InitNewTastingContactForm) {
        ctx.setState({
            key: null,
            data: {
                ...action.defaults
            },
            status: Status.OK
        });
    }

    @Action(InitUpdateTastingContactForm, {cancelUncompleted: true})
    initUpdateTastingContactForm(ctx: StateContext<TastingContactFormStateModel>, action: InitUpdateTastingContactForm) {
        ctx.setState({
            key: action.key,
            data: null,
            status: Status.LOADING
        });

        return this._tastingContactService.get(action.key)
            .pipe(
                tap(
                    result => {
                        ctx.setState({
                            key: result.key,
                            data: result,
                            status: Status.OK
                        });
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            );
    }

    @Action(SubmitTastingContactForm)
    submitTastingContactForm(ctx: StateContext<TastingContactFormStateModel>, action: SubmitTastingContactForm) {
        const state = ctx.getState();

        let request: Observable<TastingContact>;

        if (state.key) request = this._tastingContactService.update(state.key, { ...action.data });
        else request = this._tastingContactService.create({ ...action.data });

        ctx.patchState({
            status: Status.LOADING,
        });

        return request
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            key: result.key,
                            status: Status.COMPLETE,
                            data: result
                        });
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }
}